import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  WhatsappShareButton,
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  EmailShareButton,
  WhatsappIcon,
  FacebookIcon,
  XIcon,
  LinkedinIcon,
  TelegramIcon,
  EmailIcon,
} from 'react-share';
import { getCampaignShareUrl } from '../utils/campaignUrl';

function getBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return process.env.REACT_APP_SITE_URL || 'https://rwfngo.com';
}

function buildShareUrl(sharePath, campaignSlug) {
  if (campaignSlug != null && String(campaignSlug).trim()) {
    return getCampaignShareUrl(campaignSlug, getBaseUrl());
  }
  const p = String(sharePath || '').trim();
  if (!p) return `${getBaseUrl()}/campaigns`;
  const campaignMatch = p.match(/^\/campaign\/(.+)$/i);
  if (campaignMatch) {
    let slugPart = campaignMatch[1];
    try {
      slugPart = decodeURIComponent(slugPart);
    } catch (_) {
      // use raw segment
    }
    return getCampaignShareUrl(slugPart, getBaseUrl());
  }
  const path = p.startsWith('/') ? p : `/${p}`;
  return `${getBaseUrl()}${path}`;
}

const ShareRoundIcon = ({ children, title }) => (
  <Tooltip title={title}>
    <Box sx={{ display: 'inline-flex', borderRadius: '50%', overflow: 'hidden' }}>{children}</Box>
  </Tooltip>
);

const CampaignShareButtons = ({
  sharePath,
  campaignSlug,
  title,
  compact = false,
  iconSize = 34,
  showLabel = true,
}) => {
  const shareUrl = buildShareUrl(sharePath, campaignSlug);
  const shareTitle = title || 'Please support this campaign';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (_) {
      // ignore silently
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareTitle,
          url: shareUrl,
        });
      } catch (_) {
        // user cancelled
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.75 : 1, flexWrap: 'wrap' }}>
      {showLabel && (
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: compact ? 12 : 13, color: '#4b5563', mr: 0.5 }}>
          Share:
        </Typography>
      )}

      {'share' in navigator && (
        <Tooltip title="Share">
          <IconButton size="small" onClick={nativeShare} sx={{ p: 0.25 }}>
            <ShareIcon sx={{ color: '#374151', fontSize: iconSize - 6 }} />
          </IconButton>
        </Tooltip>
      )}

      <ShareRoundIcon title="WhatsApp">
        <WhatsappShareButton url={shareUrl} title={shareTitle}>
          <WhatsappIcon size={iconSize} round />
        </WhatsappShareButton>
      </ShareRoundIcon>
      <ShareRoundIcon title="Facebook">
        <FacebookShareButton url={shareUrl} hashtag="#donate">
          <FacebookIcon size={iconSize} round />
        </FacebookShareButton>
      </ShareRoundIcon>
      <ShareRoundIcon title="X">
        <TwitterShareButton url={shareUrl} title={shareTitle}>
          <XIcon size={iconSize} round />
        </TwitterShareButton>
      </ShareRoundIcon>
      <ShareRoundIcon title="LinkedIn">
        <LinkedinShareButton url={shareUrl} title={shareTitle}>
          <LinkedinIcon size={iconSize} round />
        </LinkedinShareButton>
      </ShareRoundIcon>
      <ShareRoundIcon title="Telegram">
        <TelegramShareButton url={shareUrl} title={shareTitle}>
          <TelegramIcon size={iconSize} round />
        </TelegramShareButton>
      </ShareRoundIcon>
      <ShareRoundIcon title="Email">
        <EmailShareButton url={shareUrl} subject={shareTitle} body={`${shareTitle}\n\n`}>
          <EmailIcon size={iconSize} round />
        </EmailShareButton>
      </ShareRoundIcon>

      <Tooltip title="Copy link">
        <IconButton
          size="small"
          onClick={copyLink}
          sx={{
            width: iconSize,
            height: iconSize,
            border: '1px solid #d1d5db',
            bgcolor: '#fff',
            '&:hover': { bgcolor: '#f3f4f6' },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: iconSize - 16, color: '#4b5563' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CampaignShareButtons;
